import { useCallback } from 'react'
import { useTranslation } from '@/i18n/useTranslation'
import { useToast } from '@/providers/ToastProvider'
import { useUserStore } from '@/store/user-store'
import { useBalanceStore } from '@/store/balance-store'

export interface CanTransfer {
  /** El balance activo permite transferir (Balance.CanTransfer de SignalR). */
  canTransfer: boolean
  /** Muestra un toast avisando que no tiene permisos para transferir. */
  notifyNoPermission: () => void
}

/**
 * Permiso de transferencia por balance. La info llega en el evento `Balance`
 * de SignalR (CanTransfer). Bloqueamos solo cuando hay un balance activo y NO
 * permite transferir; mientras carga (sin balance) no bloqueamos para no
 * falsear el estado ni redirigir de más.
 */
export function useCanTransfer(): CanTransfer {
  const { t } = useTranslation('layout')
  const { toast } = useToast()

  const balances        = useUserStore(s => s.user.balances)
  const selectedBalance = useBalanceStore(s => s.selectedBalance)
  const activeBalance   = balances.find(b => b.id === selectedBalance) ?? balances[0]

  const canTransfer = !activeBalance || !!activeBalance.canTransfer

  const notifyNoPermission = useCallback(() => {
    toast({
      variant:     'warning',
      title:       t('layout.transfer.noPermission.title'),
      description: t('layout.transfer.noPermission.message'),
    })
  }, [toast, t])

  return { canTransfer, notifyNoPermission }
}
